import { useEffect, useState } from "react";
import { NavLink, Navigate, Outlet, Route, Routes } from "react-router-dom";
import {
  getWorkspaceKey,
  setWorkspaceKey,
  syncDrills,
} from "./lib/cloud-drill-storage";
import { syncPracticePlan } from "./lib/cloud-practice-storage";
import { EditorRoute } from "./routes/EditorRoute";
import { LibraryRoute } from "./routes/LibraryRoute";
import { PracticePlannerRoute } from "./routes/PracticePlannerRoute";

function Shell() {
  const [cloudReady, setCloudReady] = useState(false);
  const [cloudSynced, setCloudSynced] = useState(false);
  const [backupOpen, setBackupOpen] = useState(false);
  const [restoreKey, setRestoreKey] = useState("");
  const workspaceKey = getWorkspaceKey();

  useEffect(() => {
    void Promise.all([syncDrills(), syncPracticePlan()]).then((results) => {
      setCloudSynced(results.every((result) => result.synced));
      setCloudReady(true);
    });
  }, []);

  const restoreWorkspace = async () => {
    if (!setWorkspaceKey(restoreKey)) return;
    setCloudReady(false);
    const results = await Promise.all([syncDrills(), syncPracticePlan()]);
    setCloudSynced(results.every((result) => result.synced));
    setCloudReady(true);
    setBackupOpen(false);
    window.location.reload();
  };

  return (
    <div className="app-shell">
      <header className="site-header">
        <NavLink to="/drills" className="brand" aria-label="Puckly Coach home">
          <img src="/brand/puckly-logo-white.png" alt="Puckly Hockey" />
          <span>Coach</span>
        </NavLink>
        <nav aria-label="Coach navigation">
          <NavLink to="/drills">Drills</NavLink>
          <NavLink to="/practice">Practice plans</NavLink>
        </nav>
        <button
          className={`cloud-backup-button ${cloudSynced ? "synced" : "local"}`}
          onClick={() => setBackupOpen(true)}
        >
          <span aria-hidden="true">{cloudSynced ? "●" : "○"}</span>
          {cloudSynced ? "Cloud backed up" : "Local backup"}
        </button>
      </header>
      {cloudReady ? (
        <Outlet />
      ) : (
        <main className="cloud-loading" aria-live="polite">
          <span />
          Loading your drill library…
        </main>
      )}
      {backupOpen && (
        <div className="cloud-modal-backdrop" role="presentation">
          <section
            className="cloud-modal"
            role="dialog"
            aria-modal="true"
            aria-labelledby="cloud-backup-title"
          >
            <button
              className="cloud-modal-close"
              onClick={() => setBackupOpen(false)}
              aria-label="Close cloud backup"
            >
              ×
            </button>
            <p className="eyebrow">Cloud backup</p>
            <h2 id="cloud-backup-title">Your private recovery key</h2>
            <p>
              Save this key somewhere safe. It restores your drills on another
              browser or device without exposing them publicly.
            </p>
            <div className="recovery-key-row">
              <input readOnly value={workspaceKey} aria-label="Recovery key" />
              <button
                onClick={() => void navigator.clipboard.writeText(workspaceKey)}
              >
                Copy
              </button>
            </div>
            <div className="restore-workspace">
              <label htmlFor="restore-key">Restore an existing library</label>
              <div>
                <input
                  id="restore-key"
                  value={restoreKey}
                  onChange={(event) => setRestoreKey(event.target.value)}
                  placeholder="Paste your 64-character recovery key"
                />
                <button onClick={() => void restoreWorkspace()}>Restore</button>
              </div>
            </div>
          </section>
        </div>
      )}
    </div>
  );
}

export default function App() {
  return (
    <Routes>
      <Route element={<Shell />}>
        <Route index element={<Navigate to="/drills" replace />} />
        <Route path="drills" element={<LibraryRoute />} />
        <Route path="drills/new" element={<EditorRoute />} />
        <Route path="drills/:drillId/edit" element={<EditorRoute />} />
        <Route path="practice" element={<PracticePlannerRoute />} />
        <Route path="*" element={<Navigate to="/drills" replace />} />
      </Route>
    </Routes>
  );
}
